/**
 * Track layer factory for moving objects (vessels, flights).
 * IconLayer with heading rotation + PathLayer for trailing positions.
 */

import { IconLayer, PathLayer, ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent, GeoLocation } from '@/sources/base-source';
import { createSvgIcon } from './icon-layer';
import { altitudeToColor, MARKER_ICONS, AIRCRAFT_ICON_MAPPING } from '@/map/layer-colors';
import { shipTypeCategory } from '@/sources/built-in/vessel-source';
import { aggregateActivityClusters, type ActivityCluster } from './military-vessel-layer';

/** Layer IDs handled by the track renderer. */
export const TRACK_LAYER_IDS = new Set(['vessels', 'flights', 'naval', 'military-aircraft']);

/** Store previous positions for trail rendering, keyed by event ID. Max 10 per track. */
const positionHistory = new Map<string, GeoLocation[]>();

const MAX_TRAIL_LENGTH = 10;
/** Maximum tracked entities. When exceeded, oldest half is pruned (by Map insertion order). */
const MAX_TRACKED_ENTITIES = 10_000;

/** Vessel type color mapping — matching WorldMonitor. */
const VESSEL_COLORS: Record<string, [number, number, number]> = {
  cargo: [68, 136, 255],      // blue
  tanker: [255, 140, 0],      // orange
  military: [255, 100, 100],  // red
  passenger: [68, 204, 136],  // green
  fishing: [0, 200, 180],     // teal
};
/** WM's default vessel blue (#44aaff) — unknown-type vessels stay this color. */
const DEFAULT_VESSEL_COLOR: [number, number, number] = [68, 170, 255];

/** Meters-to-feet factor: OpenSky altitudes are meters, the color stops are feet. */
const M_TO_FT = 3.28084;

/** Flight altitude color — uses WM's altitudeToColor gradient (feet). */
export function getFlightColor(event: NormalizedEvent): [number, number, number] {
  const raw = event.raw as Record<string, unknown> | undefined;
  // Prefer the feet-named field from the source boundary; fall back to the
  // raw OpenSky meters field. Non-finite/absent coerces to 0 so
  // unknown-altitude aircraft read as ground-level, not cruise.
  if (typeof raw?.altitudeFt === 'number' && Number.isFinite(raw.altitudeFt)) {
    return altitudeToColor(raw.altitudeFt);
  }
  const altM = raw?.geoAltitude;
  const altFt = typeof altM === 'number' && Number.isFinite(altM) ? altM * M_TO_FT : 0;
  return altitudeToColor(altFt);
}

/** Get heading angle from event metadata. */
function getHeading(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (typeof raw?.heading === 'number') return raw.heading;
  if (typeof raw?.track === 'number') return raw.track;
  return 0;
}

/** Get vessel type category from event metadata. */
export function getVesselType(event: NormalizedEvent): string {
  const raw = event.raw as Record<string, unknown> | undefined;
  if (typeof raw?.vesselType === 'string') return raw.vesselType;
  // Older cached events carry only the AIS code
  if (typeof raw?.shipType === 'number') return shipTypeCategory(raw.shipType);
  return 'other';
}

/** Record a position for trail history. Called when new data arrives. */
export function recordPosition(event: NormalizedEvent): void {
  if (!event.location) return;

  const history = positionHistory.get(event.id) ?? [];

  // Rebuilds re-record whatever the store currently holds (cache-hit
  // rebuilds, pulse-driver ticks), so the same observation arrives many
  // times. Only actual movement may consume a trail slot — duplicates
  // would flush the whole bounded history within seconds.
  const last = history[history.length - 1];
  if (last && last.lat === event.location.lat && last.lng === event.location.lng) return;

  history.push(event.location);

  // Keep trail length bounded per entity
  if (history.length > MAX_TRAIL_LENGTH) {
    history.splice(0, history.length - MAX_TRAIL_LENGTH);
  }

  positionHistory.set(event.id, history);

  // Cap total tracked entities — prune oldest half when exceeded (Map preserves insertion order)
  if (positionHistory.size > MAX_TRACKED_ENTITIES) {
    const pruneCount = Math.floor(MAX_TRACKED_ENTITIES / 2);
    let i = 0;
    for (const key of positionHistory.keys()) {
      if (i++ >= pruneCount) break;
      positionHistory.delete(key);
    }
  }
}

/** Batch-record positions for a set of events. */
export function recordPositions(events: NormalizedEvent[]): void {
  for (const event of events) {
    recordPosition(event);
  }
}

/** Build trail path data for events with position history. */
function buildTrails(events: NormalizedEvent[]): { path: [number, number][]; color: [number, number, number] }[] {
  const trails: { path: [number, number][]; color: [number, number, number] }[] = [];

  for (const event of events) {
    const history = positionHistory.get(event.id);
    if (!history || history.length < 2) continue;

    trails.push({
      path: history.map((loc) => [loc.lng, loc.lat]),
      color: event.type === 'vessel_position'
        ? VESSEL_COLORS[getVesselType(event)] ?? DEFAULT_VESSEL_COLOR
        : getFlightColor(event),
    });
  }

  return trails;
}

/** Create deck.gl layers for vessel tracking. */
export function createVesselLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  const opacity = Math.min(1, 0.3 + zoom * 0.1);
  const layers: Layer[] = [];

  // Icon markers with heading rotation
  layers.push(new IconLayer<NormalizedEvent>({
    id: 'track-vessels-icons',
    data: events,
    pickable: true,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getIcon: (d) => {
      const color = VESSEL_COLORS[getVesselType(d)] ?? DEFAULT_VESSEL_COLOR;
      return {
        url: createSvgIcon('anchor', color),
        width: 48,
        height: 48,
        anchorY: 24,
      };
    },
    getAngle: (d) => -getHeading(d),
    getSize: zoom < 5 ? 16 : 22,
    sizeUnits: 'pixels' as const,
    opacity,
  }));

  // Trail paths
  const trails = buildTrails(events);
  if (trails.length > 0) {
    layers.push(new PathLayer<{ path: [number, number][]; color: [number, number, number] }>({
      id: 'track-vessels-trails',
      data: trails,
      getPath: (d) => d.path,
      getColor: (d) => [...d.color, 120],
      getWidth: 1.5,
      widthUnits: 'pixels' as const,
      opacity: opacity * 0.6,
    }));
  }

  return layers;
}

/** Whether an aircraft event reports being on the ground. */
function isOnGround(d: NormalizedEvent): boolean {
  const raw = d.raw as Record<string, unknown> | undefined;
  return raw?.onGround === true;
}

/** Create deck.gl layers for flight tracking (civilian sign: rotated plane icons). */
export function createFlightLayers(
  events: NormalizedEvent[],
  _zoom: number,
  idPrefix = 'flights',
  showTrails = false,
): Layer[] {
  const layers: Layer[] = [];

  // No layer opacity — WM renders aircraft at full strength (DeckGLMap.ts:3122-3143)
  layers.push(new IconLayer<NormalizedEvent>({
    id: `track-${idPrefix}-icons`,
    data: events,
    pickable: true,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getIcon: () => 'plane',
    iconAtlas: MARKER_ICONS.plane,
    iconMapping: AIRCRAFT_ICON_MAPPING,
    getColor: (d) => {
      if (isOnGround(d)) return [120, 120, 120, 160] as [number, number, number, number];
      const [r, g, b] = getFlightColor(d);
      return [r, g, b, 220] as [number, number, number, number];
    },
    getAngle: (d) => -getHeading(d),
    getSize: (d) => isOnGround(d) ? 14 : 18,
    sizeMinPixels: 8,
    sizeMaxPixels: 28,
    sizeScale: 1,
    billboard: false,
  }));

  // WM has no always-on civil trails — gated behind the 'flight-trails' toggle
  if (showTrails) {
    const trails = buildTrails(events);
    if (trails.length > 0) {
      layers.push(new PathLayer<{ path: [number, number][]; color: [number, number, number] }>({
        id: `track-${idPrefix}-trails`,
        data: trails,
        getPath: (d) => d.path,
        getColor: (d) => [...d.color, 100],
        getWidth: 1,
        widthUnits: 'pixels' as const,
      }));
    }
  }

  return layers;
}

/**
 * Military aircraft — WM's sign is a plain altitude-colored DOT, deliberately
 * different from the rotated civilian plane silhouette (DeckGLMap.ts:3830-3845).
 */
export function createMilitaryFlightLayers(events: NormalizedEvent[]): Layer[] {
  if (events.length === 0) return [];

  return [new ScatterplotLayer<NormalizedEvent>({
    id: 'military-flights-layer',
    data: events,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
    getRadius: 8000,
    getFillColor: (d) => {
      if (isOnGround(d)) return [120, 120, 120, 160] as [number, number, number, number];
      const [r, g, b] = getFlightColor(d);
      return [r, g, b, 220] as [number, number, number, number];
    },
    radiusMinPixels: 4,
    radiusMaxPixels: 12,
  })];
}

/**
 * Sortie concentration discs — BLUE family so air activity stays
 * separable from the red naval clusters at a glance despite identical
 * geometry (WM DeckGLMap.ts:3860-3877).
 */
export function createMilitaryFlightClusterLayers(events: NormalizedEvent[], zoom: number): Layer[] {
  const clusters = aggregateActivityClusters(events, zoom);
  if (clusters.length === 0) return [];

  return [new ScatterplotLayer<ActivityCluster>({
    id: 'military-flight-clusters-layer',
    data: clusters,
    pickable: true,
    filled: true,
    radiusUnits: 'meters' as const,
    getPosition: (d) => [d.lng, d.lat],
    getRadius: (d) => 15_000 + (d.count || 1) * 3000,
    getFillColor: (d) => {
      if (d.activityType === 'exercise' || d.activityType === 'patrol') {
        return [100, 150, 255, 200] as [number, number, number, number];
      }
      if (d.activityType === 'transport') {
        return [255, 200, 100, 180] as [number, number, number, number];
      }
      return [150, 150, 200, 160] as [number, number, number, number];
    },
    radiusMinPixels: 8,
    radiusMaxPixels: 25,
  })];
}

/** Clear all stored position history. */
export function clearPositionHistory(): void {
  positionHistory.clear();
}
